import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { api, ApiError, type Me } from '../lib/api'
import { Banner, Button, Card, Dialog, TextField, useToast } from '../ui'

export function ProfilePage({ me }: { me: Me }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [params] = useSearchParams()
  const [name, setName] = useState(me.full_name)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [emailOpen, setEmailOpen] = useState(false)

  const saveName = useMutation({
    mutationFn: () => api.patch<Me>('/api/me', { full_name: name }),
    onSuccess: (updated) => {
      toast('Name gespeichert.', 'success')
      queryClient.setQueryData(['me'], updated)
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  return (
    <div className="stack">
      <h1>Profil</h1>

      {params.get('hinweis') === 'google_verknuepft' && (
        <Banner tone="success">Dein Google-Konto ist jetzt mit diesem Profil verknüpft.</Banner>
      )}
      {params.get('hinweis') === 'bereits_verknuepft' && (
        <Banner tone="info">Dieses Google-Konto war bereits verknüpft.</Banner>
      )}
      {params.get('fehler') === 'google_konto_belegt' && (
        <Banner tone="error">
          Dieses Google-Konto gehört bereits zu einem anderen Zugang. Zwei Zugänge können sich
          keine Anmeldung teilen.
        </Banner>
      )}

      <Card title="Angaben">
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault()
            saveName.mutate()
          }}
        >
          <TextField
            label="Name"
            value={name}
            minLength={2}
            required
            onChange={(event) => setName(event.target.value)}
          />
          <div className="row row--between" style={{ alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 240px' }}>
              <TextField label="E-Mail-Adresse" value={me.email} readOnly disabled />
            </div>
            <Button type="button" variant="outlined" onClick={() => setEmailOpen(true)}>
              Ändern
            </Button>
          </div>
          <div className="row row--between">
            <span />
            <Button
              type="submit"
              busy={saveName.isPending}
              disabled={name.trim() === me.full_name}
            >
              Speichern
            </Button>
          </div>
        </form>
      </Card>

      <EmailChangeDialog open={emailOpen} onClose={() => setEmailOpen(false)} me={me} />

      <Card title="Anmeldung">
        <div className="stack">
          {me.has_password ? (
            <PasswordForm />
          ) : (
            <Banner tone="info">
              Dieses Konto meldet sich über Google an und hat kein Passwort.
            </Banner>
          )}
          <div className="row row--between">
            <span className="muted">Google-Konto verknüpfen, um dich damit anzumelden.</span>
            <Button
              variant="outlined"
              small
              onClick={() => {
                window.location.href = '/api/me/link/google'
              }}
            >
              Google verknüpfen
            </Button>
          </div>
        </div>
      </Card>

      <Card title="Deine Daten">
        <div className="stack">
          <p className="muted">
            Du kannst jederzeit alles herunterladen, was zu dir gespeichert ist, und dein Konto
            löschen lassen.
          </p>
          <div className="row">
            <Button
              variant="outlined"
              onClick={() => {
                window.location.href = '/api/me/export'
              }}
            >
              Daten herunterladen
            </Button>
            <Button variant="text" onClick={() => setDeleteOpen(true)}>
              Konto löschen
            </Button>
          </div>
          <p className="muted" style={{ fontSize: '0.8125rem' }}>
            Beim Löschen wird der Personenbezug entfernt. Lohnrelevante Zeiten bleiben bestehen —
            sie unterliegen der gesetzlichen Aufbewahrungspflicht.
          </p>
        </div>
      </Card>

      <DeleteDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} />
    </div>
  )
}

function PasswordForm() {
  const toast = useToast()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [error, setError] = useState<string | null>(null)

  const change = useMutation({
    mutationFn: () =>
      api.post('/api/me/password', { current_password: current, new_password: next }),
    onSuccess: () => {
      toast('Passwort geändert. Andere Geräte wurden abgemeldet.', 'success')
      setCurrent('')
      setNext('')
      setError(null)
    },
    onError: (err) => setError((err as ApiError).message),
  })

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        change.mutate()
      }}
    >
      {error && <Banner tone="error">{error}</Banner>}
      <TextField
        label="Aktuelles Passwort"
        type="password"
        autoComplete="current-password"
        required
        value={current}
        onChange={(event) => setCurrent(event.target.value)}
      />
      <TextField
        label="Neues Passwort"
        type="password"
        autoComplete="new-password"
        required
        minLength={10}
        hint="Mindestens 10 Zeichen. Andere Geräte werden dabei abgemeldet."
        value={next}
        onChange={(event) => setNext(event.target.value)}
      />
      <div className="row row--between">
        <span />
        <Button type="submit" busy={change.isPending}>
          Passwort ändern
        </Button>
      </div>
    </form>
  )
}

function DeleteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)

  const remove = useMutation({
    mutationFn: () => api.post('/api/me/delete', { confirm }),
    onSuccess: () => {
      // Vollständiger Neuaufbau: Nach dem Löschen ist jeder zwischengespeicherte
      // Datensatz gegenstandslos.
      window.location.assign('/anmelden')
    },
    onError: (err) => setError((err as ApiError).message),
  })

  return (
    <Dialog open={open} onClose={onClose} title="Konto löschen">
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          remove.mutate()
        }}
      >
        {error && <Banner tone="error">{error}</Banner>}
        <Banner tone="warning">
          Das lässt sich nicht rückgängig machen. Du verlierst den Zugang zu allen Betrieben.
        </Banner>
        <TextField
          label="Tippe LÖSCHEN zur Bestätigung"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          autoComplete="off"
        />
        <div className="row row--between">
          <Button variant="text" type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            type="submit"
            busy={remove.isPending}
            disabled={confirm.trim().toUpperCase() !== 'LÖSCHEN'}
          >
            Endgültig löschen
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

/**
 * Wechsel der Anmeldeadresse.
 *
 * Nur für einen selbst — die Betriebsleitung kann die Adresse eines
 * Mitarbeitenden nicht ändern. Dürfte sie es, könnte sie jedes Konto
 * übernehmen: Adresse auf die eigene setzen, Passwort vergessen, fertig.
 */
function EmailChangeDialog({
  open,
  onClose,
  me,
}: {
  open: boolean
  onClose: () => void
  me: Me
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const request = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>('/api/me/email', {
        new_email: email,
        // Reine Google-Konten haben kein Passwort; der Server erwartet dort
        // auch keines.
        current_password: me.has_password ? password : null,
      }),
    onSuccess: () => {
      setSent(true)
      setPassword('')
      setError(null)
    },
    onError: (err) => setError((err as ApiError).message),
  })

  const schliessen = () => {
    setSent(false)
    setEmail('')
    setPassword('')
    setError(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={schliessen} title="E-Mail-Adresse ändern">
      {sent ? (
        <div className="stack">
          <Banner tone="success">
            Wir haben einen Bestätigungslink an <strong>{email}</strong> geschickt. Erst nach dem
            Klick darauf gilt die neue Adresse — bis dahin meldest du dich weiter mit{' '}
            <strong>{me.email}</strong> an.
          </Banner>
          <p className="muted" style={{ fontSize: '0.8125rem', margin: 0 }}>
            Der Link gilt eine Stunde. An deine bisherige Adresse ist zur Sicherheit eine
            Benachrichtigung gegangen.
          </p>
          <div className="row row--between">
            <span />
            <Button type="button" onClick={schliessen}>
              Alles klar
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            request.mutate()
          }}
        >
          {error && <Banner tone="error">{error}</Banner>}
          <TextField
            label="Neue E-Mail-Adresse"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            hint="Der Bestätigungslink geht an diese Adresse."
          />
          {me.has_password && (
            <TextField
              label="Aktuelles Passwort"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              hint="Zur Sicherheit — die Adresse ist deine Anmeldung."
            />
          )}
          <div className="row row--between">
            <Button variant="text" type="button" onClick={schliessen}>
              Abbrechen
            </Button>
            <Button type="submit" busy={request.isPending}>
              Link senden
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  )
}
